import ExportIcon from "@mui/icons-material/OutputRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import adminApi from "../../../../../api/adminApi";
import useClientContext from "../../../../../context/useClientContext";
import useUserContext from "../../../../../context/useUserContext";
import FileContainersList from "../file-container/FileContainersList";
import { useImportCsvDataContext } from "../ImportCsvDataContext";
import type { ImportCsvTargetOption } from "../importCsvDataPageState";
import {
  dataImportTargetsWithSupportedExport,
  setFileUploadCompletedAction,
  setFileUploadErrorAction,
} from "../importCsvDataPageState";
import DocumentCollectionsExportDialog from "./DocumentCollectionsExportDialog";
import ImportSourceFileTable from "./ImportSourceFileTable";
import ImportSourceFileUploadArea from "./ImportSourceFileUploadArea";

interface Props {
  targetOption: ImportCsvTargetOption;
}

const getCsvTemplateDownloadUrl = withErrorHandling(adminApi.getCsvTemplateDownloadUrl);
const getDataExportDownloadUrls = withErrorHandling(adminApi.getDataExportDownloadUrls);
const uploadDataImportSourceFile = withErrorHandling(adminApi.uploadDataImportSourceFile);
const validateDataImportFileContent = withErrorHandling(adminApi.validateDataImportFileContent);

const ImportCsvDataFileUpload = ({ targetOption }: Props) => {
  const { title: clientTitle } = useClientContext();
  const { email: userEmail } = useUserContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { pageState, setPageState } = useImportCsvDataContext();

  const [loading, setLoading] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [includeFiles, setIncludeFiles] = useState(false);
  const [isExportRunning, setIsExportRunning] = useState(false);

  const isExportExistingDataSupported = dataImportTargetsWithSupportedExport.includes(targetOption.target);
  const isDocumentCollectionsTarget = targetOption.target === "DocumentCollections";
  const canIncludeFilesInExport = targetOption.capabilities.includes("FileContainers");

  const sourceFileInfo = pageState.sourceFiles.find((file) => file.targetOptionKey === targetOption.key);

  const handleDownloadBlankTemplate = async () => {
    setLoading(true);
    const [url, error] = await getCsvTemplateDownloadUrl(
      targetOption.objectType,
      targetOption.bcEntityTypeCode,
      targetOption.target,
      clientTitle
    );
    setLoading(false);

    if (error) {
      logError(error, "[ImportCsvDataFileUpload] getCsvTemplateDownloadUrl");
      sendNotificationError(error.message);
      return;
    }

    await downloadFileFromUrl(url);
  };

  const exportExistingData = async (
    includeFilesForExport: boolean,
    setBusy: (value: boolean) => void
  ): Promise<boolean> => {
    setBusy(true);
    const [resp, error] = await getDataExportDownloadUrls(targetOption.objectType, {
      exportTarget: targetOption.target,
      bcEntityType: targetOption.bcEntityTypeCode,
      includeFiles: includeFilesForExport,
    });
    setBusy(false);

    if (error) {
      logError(error, "[ImportCsvDataFileUpload] getDataExportDownloadUrls");
      sendNotificationError(error.message);
      return false;
    }

    const urls = resp.filesDownloadUrls;
    if (urls.length === 0) {
      sendNotificationError("No data to export.");
      return false;
    }

    await Promise.all(urls.map((url) => downloadFileFromUrl(url)));

    if (includeFilesForExport) {
      sendNotification(
        `CSV is ready. File package request was submitted. You will receive an email at ${
          userEmail || "your current account email"
        } when processing completes (success or failure).`
      );
    }

    return true;
  };

  const handleExportExistingData = async () => {
    if (!isExportExistingDataSupported) {
      return;
    }

    if (isDocumentCollectionsTarget) {
      setIncludeFiles(false);
      setIsExportDialogOpen(true);
      return;
    }

    await exportExistingData(false, setLoading);
  };

  const handleStartDocumentCollectionsExport = async () => {
    const shouldIncludeFiles = canIncludeFilesInExport ? includeFiles : false;
    const success = await exportExistingData(shouldIncludeFiles, setIsExportRunning);
    if (success) {
      setIsExportDialogOpen(false);
    }
  };

  const handleUploadStart = async (targetOptionKey: string, file: File) => {
    const [fileInfo, uploadError] = await uploadDataImportSourceFile(file);
    if (uploadError) {
      logError(uploadError, "[ImportCsvDataFileUpload] uploadDataImportSourceFile");
      setPageState(setFileUploadErrorAction(targetOptionKey, "Upload failed."));
      return;
    }

    const [validateFileResult, validateError] = await validateDataImportFileContent({
      fileDataCatalogueId: fileInfo.catalogueId,
      importTypeTarget: targetOption.target,
      importObjectTypeTarget: targetOption.objectType,
      importEntityTypeTarget: targetOption.bcEntityTypeCode,
    });

    if (validateError) {
      logError(validateError, "[ImportCsvDataFileUpload] validateDataImportFileContent");
      setPageState(setFileUploadErrorAction(targetOptionKey, "File validation failed."));
      return;
    }

    if (!validateFileResult.success) {
      setPageState(
        setFileUploadErrorAction(targetOptionKey, validateFileResult.errorMessage || "File validation failed.")
      );
      return;
    }

    setPageState(setFileUploadCompletedAction(targetOptionKey, fileInfo));
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2 }}>
        <Typography variant="subtitle2">{targetOption.label}</Typography>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          {loading && <CircularProgress size={20} color="primary" />}
          <Button variant="text" startIcon={<DownloadIcon />} disabled={loading} onClick={handleDownloadBlankTemplate}>
            Download blank template
          </Button>
          <Button
            variant="text"
            startIcon={<ExportIcon />}
            disabled={!isExportExistingDataSupported || loading || isExportRunning}
            onClick={handleExportExistingData}
          >
            Export existing data
          </Button>
        </Stack>
      </Box>
      {!targetOption.canImport && (
        <Typography variant="body2" color="textSecondary">
          You can export this data, but you don&apos;t have permission to import it.
        </Typography>
      )}
      {targetOption.canImport && sourceFileInfo && <ImportSourceFileTable sourceFile={sourceFileInfo} />}
      {targetOption.canImport && !sourceFileInfo && (
        <ImportSourceFileUploadArea targetOptionKey={targetOption.key} onUploadStart={handleUploadStart} />
      )}
      {targetOption.canImport && targetOption.capabilities.includes("FileContainers") && (
        <FileContainersList targetOption={targetOption} />
      )}
      <DocumentCollectionsExportDialog
        open={isExportDialogOpen}
        canIncludeFiles={canIncludeFilesInExport}
        includeFiles={includeFiles}
        isStarting={isExportRunning}
        userEmail={userEmail}
        onClose={() => setIsExportDialogOpen(false)}
        onIncludeFilesChange={setIncludeFiles}
        onStartExport={handleStartDocumentCollectionsExport}
      />
    </Box>
  );
};

export default ImportCsvDataFileUpload;
